import { BalanceTable } from "./BalanceTable";
import { FreeformBoard } from "./FreeformBoard";
import { TileInspector } from "./TileInspector";
import { tileImageUrl } from "../lib/tiles";
import type {
  EditorTool,
  GeneratedMap,
  LayoutCell,
  SliceGeometry,
  SystemTile,
} from "../types";

type MapWorkspaceProps = {
  cells: LayoutCell[];
  generatedMap: GeneratedMap | null;
  previewGeometry?: SliceGeometry;
  tileById: Map<string, SystemTile>;
  tool: EditorTool;
  selectedSlotId: string | null;
  selectedLayoutCell: LayoutCell | null;
  highlightedSlotIds: Set<string>;
  playerColors: string[];
  useThunderEdgeMecatol: boolean;
  phase: "design" | "generated";
  selectedTile: SystemTile | null;
  onCellClick: (
    coord: { q: number; r: number },
    existing?: LayoutCell,
    options?: { chooseSpecificSystem: boolean },
  ) => void;
  onSelectGenerated: (slotId: string) => void;
  onSwapGenerated: (fromSlotId: string, toSlotId: string) => void;
  onCloseLayoutInspector: () => void;
  onMoveLayoutCell: (cell: LayoutCell) => void;
  onEditWarpLane: (cell: LayoutCell) => void;
  onRemoveLayoutCell: (cell: LayoutCell) => void;
  onRequestFixedSystem: (cell: LayoutCell) => void;
  onClearFixedSystem: (cell: LayoutCell) => void;
  showSectorShortcutTip: boolean;
  onDismissSectorShortcutTip: () => void;
  onCloseTileInspector: () => void;
  onRequestReplacement?: () => void;
};

function tileName(tile: SystemTile): string {
  return tile.planets.map((planet) => planet.name).join(" / ") || "Empty System";
}

export function MapWorkspace({
  cells,
  generatedMap,
  previewGeometry,
  tileById,
  tool,
  selectedSlotId,
  selectedLayoutCell,
  highlightedSlotIds,
  playerColors,
  useThunderEdgeMecatol,
  phase,
  selectedTile,
  onCellClick,
  onSelectGenerated,
  onSwapGenerated,
  onCloseLayoutInspector,
  onMoveLayoutCell,
  onEditWarpLane,
  onRemoveLayoutCell,
  onRequestFixedSystem,
  onClearFixedSystem,
  showSectorShortcutTip,
  onDismissSectorShortcutTip,
  onCloseTileInspector,
  onRequestReplacement,
}: MapWorkspaceProps) {
  const fixedTile = selectedLayoutCell?.fixedTileId
    ? tileById.get(selectedLayoutCell.fixedTileId) ?? null
    : null;

  return (
    <section className="map-and-analysis">
      <div className="board-stage">
        <FreeformBoard
          cells={cells}
          generatedMap={generatedMap}
          previewGeometry={previewGeometry}
          tileById={tileById}
          tool={tool}
          selectedSlotId={selectedSlotId}
          highlightedSlotIds={highlightedSlotIds}
          playerColors={playerColors}
          useThunderEdgeMecatol={useThunderEdgeMecatol}
          phase={phase}
          onCellClick={onCellClick}
          onSelectGenerated={onSelectGenerated}
          onSwapGenerated={onSwapGenerated}
        />

        {showSectorShortcutTip && (
          <aside className="sector-shortcut-tip" aria-live="polite">
            <button
              type="button"
              className="icon-button"
              onClick={onDismissSectorShortcutTip}
              aria-label="Dismiss specific-system tip"
            >
              ×
            </button>
            <strong>Place a specific system</strong>
            <span>
              Hold <kbd>Option</kbd> or <kbd>Alt</kbd> while clicking an empty
              hex.
            </span>
          </aside>
        )}

        {phase === "design" && selectedLayoutCell && (
          <aside className="layout-cell-inspector">
            <button
              type="button"
              className="icon-button close-button"
              onClick={onCloseLayoutInspector}
            >
              ×
            </button>
            <span className="eyebrow">Layout hex</span>
            <h3>
              {selectedLayoutCell.kind === "home"
                ? `P${(selectedLayoutCell.player ?? 0) + 1} Home`
                : selectedLayoutCell.kind === "mecatol"
                  ? "Mecatol Rex"
                  : selectedLayoutCell.kind === "warp"
                    ? `Warp Lane ${selectedLayoutCell.warp?.laneId}`
                    : fixedTile
                      ? "Fixed system"
                      : "Sector"}
            </h3>
            {selectedLayoutCell.kind === "warp" && (
              <p>Rotation: {selectedLayoutCell.warp?.rotation ?? 0}°</p>
            )}

            {selectedLayoutCell.kind === "sector" && fixedTile && (
              <div className="fixed-system-summary">
                <img
                  src={tileImageUrl(fixedTile.id)}
                  alt={`Tile ${fixedTile.id}`}
                />
                <div>
                  <strong>#{fixedTile.id}</strong>
                  <span>{tileName(fixedTile)}</span>
                  <small>Generation will keep this system here.</small>
                </div>
              </div>
            )}

            {selectedLayoutCell.kind === "sector" && !fixedTile && (
              <p>
                This sector will be filled during generation. Choose a specific
                system to pin one here instead.
              </p>
            )}

            <div className="inspector-actions">
              {selectedLayoutCell.kind === "sector" && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onRequestFixedSystem(selectedLayoutCell)}
                >
                  {fixedTile ? "Change system" : "Choose specific system"}
                </button>
              )}
              {selectedLayoutCell.kind === "sector" && fixedTile && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onClearFixedSystem(selectedLayoutCell)}
                >
                  Unpin system
                </button>
              )}
              <button
                type="button"
                className="secondary-button"
                onClick={() => onMoveLayoutCell(selectedLayoutCell)}
              >
                Move
              </button>
              {selectedLayoutCell.kind === "warp" && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onEditWarpLane(selectedLayoutCell)}
                >
                  Edit lane
                </button>
              )}
              <button
                type="button"
                className="danger-button"
                onClick={() => onRemoveLayoutCell(selectedLayoutCell)}
              >
                Remove
              </button>
            </div>
          </aside>
        )}

        <TileInspector
          tile={selectedTile}
          onClose={onCloseTileInspector}
          onRequestReplacement={onRequestReplacement}
        />
      </div>
      <div className="analysis-region">
        <BalanceTable map={generatedMap} />
      </div>
    </section>
  );
}
